/**
 * @file 内联 mention 引用区间定位与剪枝工具函数
 * 负责在正文文本中定位 Skill / Knowledge 引用串的位置区间。
 * Chart 走顶部 Tag，不参与 inline 高亮。
 */
import { compact, uniq } from 'lodash-es';
import type { ISelectedSkillTagData } from '../SkillMention/SelectedSkillTag';
import {
  getKnowledgeTagKey,
  type ISelectedKnowledgeTagData,
} from '../KnowledgeBaseMention/components/SelectedKnowledgeTag';
import {
  buildKnowledgeInlineLabel,
  buildSkillInlineLabel,
  mentionLabelMatchVariants,
} from './inlineMentionLabel';

/** 内联引用类型：skill / knowledge */
export type TInlineMentionKind = 'skill' | 'knowledge';

/**
 * mention 序列化边界符：零宽空格，不可见、不可能由用户键入。
 *
 * 根本问题：mention 正文（库名/文档名）可包含任意字符（空格、数字、中文……），
 * 与其后用户正文拼接后无法靠"字符形状"regex 可靠区分边界（如 `@111-我创建123测试`、
 * `@新建 文本文档.md 123测试`——正文本身带空格/数字）。
 * 解法：序列化时在每个 mention 后无条件写入本符号作为精确终止符；
 * 反解析时仅认该符号，无符号的 `@`/`/` 一律当普通文本。
 */
export const MENTION_BOUNDARY_MARKER = '\u200b';

/** kb URL 预填 loading：mention 标签正文为 `-`，左侧转圈 */
export const KB_PREFILL_PLACEHOLDER_BODY = '-';

/** kb URL 预填 loading：写入输入框的序列化串 */
export const KB_PREFILL_PLACEHOLDER_SERIALIZED = `@${KB_PREFILL_PLACEHOLDER_BODY}${MENTION_BOUNDARY_MARKER}`;

/**
 * 是否为 kb 预填 loading 占位正文（`-` 或 `@-`，可带边界符）
 * @param value - 输入框 value / 当前正文
 */
export const isKbPrefillPlaceholderValue = (value: string): boolean => {
  const stripped = (value || '').replace(/\u200b/g, '').trim();
  if (!stripped) {
    return false;
  }
  if (stripped === KB_PREFILL_PLACEHOLDER_BODY) {
    return true;
  }
  return stripped === `@${KB_PREFILL_PLACEHOLDER_BODY}`;
};

/**
 * 将已选知识库拼成带边界符的 @ 序列化串（如 `@库名 4篇\u200b`）。
 * 同一 kbId 折叠为一个 mention，多个库按首次出现顺序拼接。
 * @param items - 已选知识库文档引用
 * @param docUnit - 文档数量单位文案（「篇」/「docs」）
 * @returns 序列化串；无有效 label 时为空串
 */
export const serializeKbPrefill = (
  items: ISelectedKnowledgeTagData[],
  docUnit: string,
): string => {
  /** 按首次出现顺序保留 kbId，避免同一库重复输出 */
  const kbIds = uniq(items.map(item => item.kbId));
  return compact(
    kbIds.map(kbId => {
      const group = items.filter(item => item.kbId === kbId);
      const raw = buildKnowledgeInlineLabel(group, docUnit).trim();
      /** 无库名/文档时不产出空 mention */
      if (!raw) {
        return '';
      }
      /** 已带边界符则原样返回，避免重复写入零宽空格 */
      if (raw.endsWith(MENTION_BOUNDARY_MARKER)) {
        return raw;
      }
      return `${raw}${MENTION_BOUNDARY_MARKER}`;
    }),
  ).join('');
};

/**
 * 内联引用区间（在正文中的起止位置 + 关联数据）
 * @interface IInlineMentionRange
 */
export interface IInlineMentionRange {
  /** 稳定 key，用于选中态 */
  key: string;
  /** 引用类型 */
  kind: TInlineMentionKind;
  /** 正文中的起始位置（不含尾随空格） */
  start: number;
  /** 正文中的结束位置（不含尾随空格） */
  end: number;
  /** 用于展示/匹配的文案（trim 后） */
  label: string;
  /** 关联的 selected* id（知识库可为同一库下多个） */
  ids: string[];
}

/**
 * 收集选项：正文 + 三种 selected* 数据
 * @interface ICollectOptions
 */
interface ICollectOptions {
  /** 输入框正文内容 */
  value: string;
  /** 已选技能列表 */
  selectedSkills: ISelectedSkillTagData[];
  /** 已选知识库文档列表 */
  selectedKnowledge: ISelectedKnowledgeTagData[];
  /** 是否为英文环境 */
  isEN: boolean;
  /** 知识库文档数量单位文案，如 "篇" / "docs" */
  kbDocUnit?: string;
}

/**
 * 候选条目：由 selected* 数据 + label 构建，用于在正文中匹配定位
 * @interface ICandidate
 */
interface ICandidate {
  /** 稳定 key */
  key: string;
  /** 引用类型 */
  kind: TInlineMentionKind;
  /** 用于正文匹配的 label 文案 */
  label: string;
  /** 关联 ID 列表 */
  ids: string[];
}

function pushCandidateWithVariants(
  list: ICandidate[],
  candidate: Omit<ICandidate, 'label'> & { label: string; },
): void {
  mentionLabelMatchVariants(candidate.label).forEach((label) => {
    list.push({ ...candidate, label });
  });
}

/**
 * 从 selected* 数据构建候选条目列表，按 label 长度降序（长串优先，避免短名吞掉长名）
 * @param options - 选中的数据（不含 value）
 * @returns 候选条目列表
 */
function buildCandidates(options: Omit<ICollectOptions, 'value'>): ICandidate[] {
  const {
    selectedSkills: skillsProp,
    selectedKnowledge: knowledgeProp,
    isEN,
    kbDocUnit = '篇',
  } = options;
  const selectedSkills = skillsProp ?? [];
  const selectedKnowledge = knowledgeProp ?? [];
  const list: ICandidate[] = [];

  selectedSkills.forEach((skill) => {
    const label = buildSkillInlineLabel(skill, isEN).trim();
    if (!label) return;
    pushCandidateWithVariants(list, {
      key: `skill:${skill.skillId}`,
      kind: 'skill',
      label,
      ids: [skill.skillId],
    });
  });

  const byKb = new Map<string, ISelectedKnowledgeTagData[]>();
  selectedKnowledge.forEach((item) => {
    const group = byKb.get(item.kbId) || [];
    group.push(item);
    byKb.set(item.kbId, group);
  });
  byKb.forEach((group, kbId) => {
    const groupLabel = buildKnowledgeInlineLabel(group, kbDocUnit).trim();
    if (groupLabel) {
      pushCandidateWithVariants(list, {
        key: `knowledge:${kbId}`,
        kind: 'knowledge',
        label: groupLabel,
        ids: group.map((item) => getKnowledgeTagKey(item)),
      });
    }
    // @ 模式逐条插入时，正文可能是多条单文档 label，而非分组串
    if (group.length > 1) {
      group.forEach((item) => {
        const oneLabel = buildKnowledgeInlineLabel([item], kbDocUnit).trim();
        if (!oneLabel || oneLabel === groupLabel) return;
        pushCandidateWithVariants(list, {
          key: `knowledge:${getKnowledgeTagKey(item)}`,
          kind: 'knowledge',
          label: oneLabel,
          ids: [getKnowledgeTagKey(item)],
        });
      });
    }
  });

  // 长串优先，避免短名吞掉长名
  return list.sort((a, b) => b.label.length - a.label.length);
}

/**
 * 匹配输入态引用串的正则（兼容历史：触发符后可无空格、可有 1～2 个空格）：
 * - `@库名` / `@ 库名` / `@库名-文档…` / `@库名 N篇|docs`（正文可含空格）
 * - `/Skill名` / `/ Skill名`
 * 结束于「空格 + 非 mention 正文」或字符串结尾；mention 须位于行首或空白后。
 * 仅用于输入态高亮兜底；历史/编辑反序列化请用 findSerializedMentionRanges。
 */
export const MENTION_TOKEN_RE =
  /(?:^|(?<=\s))([@/])\s*([^\s@/][^@/]*?)(?=\s+(?![@/])|$)/g;

/**
 * 解析提交/历史 display_query 中的 mention（紧凑格式，`@`/`/` 可紧跟前文如 `123@doc.md`）
 *
 * 边界判定：仅认 `MENTION_BOUNDARY_MARKER`。无边界符的 `@`/`/` 一律当普通文本，不做猜测。
 *
 * ⚠️ 仅用于 hydrate / 历史展示反序列化，禁止在 MentionEditor 编辑态使用。
 * 编辑态 mention 边界请用 `collectDomMentionRanges`（editorDom.ts）。
 */
/**
 * 仅修复折叠态 `@库名 N篇` 缺失边界符（正文含空格，无法被 findSerializedMentionRanges 断句）。
 * 只处理这一种形态，供历史气泡只读展示；编辑态应使用带完整边界符的 display_query。
 */
/**
 * 提取输入框开头的连续 mention 前缀（Skill / 知识库内联引用）。
 * 用于推荐问题快捷发送时保留已输入的引用串。
 */
export function extractLeadingMentionPrefix(value: string): string {
  if (!value) return '';
  const ranges = findSerializedMentionRanges(value).sort((a, b) => a.start - b.start);
  if (!ranges.length) return '';

  let cursor = 0;
  for (const range of ranges) {
    if (range.start < cursor) continue;
    const between = value.slice(cursor, range.start);
    if (between.trim()) break;
    cursor = range.end;
  }

  return cursor > 0 ? value.slice(0, cursor) : '';
}

/** 推荐问题 / 猜你想问快捷发送：保留输入框已有 mention 前缀，拼接快捷文案 */
export function mergeQuickSubmitDisplayQuery(
  currentInputValue: string,
  quickText: string,
): string {
  const body = quickText.trim();
  if (!body) return currentInputValue.trim();
  const prefix = extractLeadingMentionPrefix(currentInputValue);
  if (!prefix) return body;
  return `${prefix}${body}`;
}

export function patchCollapsedKbCountMentionMarkers(
  value: string,
  docUnit = '篇',
): string {
  if (!value?.includes('@')) return value;
  const unit = docUnit === '篇' ? '篇' : 'docs';
  const re = new RegExp(
    `(@[^\\s@/][^@${MENTION_BOUNDARY_MARKER}]*? \\d+${unit})(?!${MENTION_BOUNDARY_MARKER})`,
    'g',
  );
  return value.replace(re, `$1${MENTION_BOUNDARY_MARKER}`);
}

/** Skill `/` 仅在有边界处视为触发符，避免知识库文档名中的 `/` 被误判 */
const isSkillMentionTriggerAt = (value: string, index: number): boolean => {
  if (value[index] !== '/') return false;
  if (index === 0) return true;
  const prev = value[index - 1];
  return /\s/.test(prev) || prev === MENTION_BOUNDARY_MARKER;
};

/** Knowledge `@` 与 `/` 对齐：仅行首或空白/边界符后视为 mention 起点，避免邮箱等误判 */
const isKnowledgeMentionTriggerAt = (value: string, index: number): boolean => {
  if (value[index] !== '@') return false;
  if (index === 0) return true;
  const prev = value[index - 1];
  return /\s/.test(prev) || prev === MENTION_BOUNDARY_MARKER;
};

export function findSerializedMentionRanges(value: string): IInlineMentionRange[] {
  if (!value) return [];

  const ranges: IInlineMentionRange[] = [];
  let idx = 0;
  let searchFrom = 0;

  while (searchFrom < value.length) {
    let triggerStart = -1;
    let triggerChar = '';

    for (let i = searchFrom;i < value.length;i += 1) {
      const ch = value[i];
      if (isKnowledgeMentionTriggerAt(value, i)) {
        triggerStart = i;
        triggerChar = '@';
        break;
      }
      if (isSkillMentionTriggerAt(value, i)) {
        triggerStart = i;
        triggerChar = ch;
        break;
      }
    }

    if (triggerStart < 0) break;

    const kind: TInlineMentionKind = triggerChar === '@' ? 'knowledge' : 'skill';
    let cursor = triggerStart + 1;
    while (cursor < value.length && value[cursor] === ' ') {
      cursor += 1;
    }

    const markerIdx = value.indexOf(MENTION_BOUNDARY_MARKER, cursor);
    if (markerIdx === -1) {
      searchFrom = triggerStart + 1;
      continue;
    }

    const trimmedBody = value.slice(cursor, markerIdx).trim();
    if (!trimmedBody) {
      searchFrom = triggerStart + 1;
      continue;
    }

    const label = `${triggerChar}${trimmedBody}`;
    ranges.push({
      key: `serialized:${kind}:${idx}:${triggerStart}`,
      kind,
      start: triggerStart,
      end: markerIdx + 1,
      label,
      ids: [],
    });
    idx += 1;
    searchFrom = markerIdx + 1;
  }

  return ranges;
}

/**
 * 某字符索引是否落在已解析的 mention 区间内（含触发符与历史触发符后空格）
 * @param cursorPos 当前光标；紧跟在触发符后时视为正在输入 query，不算已有 mention
 */
export function isSerializedMentionIndex(
  value: string,
  index: number,
  cursorPos?: number,
): boolean {
  if (!value || index < 0 || index >= value.length) return false;
  return findSerializedMentionRanges(value).some((range) => {
    if (index < range.start || index >= range.end) return false;
    // 光标在 @ / 正后方：用户刚输入触发符，后面跟的正文不应阻止唤起面板
    if (
      cursorPos !== undefined
      && index === range.start
      && cursorPos === range.start + 1
    ) {
      return false;
    }
    return true;
  });
}

/**
 * 根据 token 前缀解析引用类型
 * @param token - 引用 token 字符串
 * @returns 引用类型
 */
const resolveMentionKind = (token: string): TInlineMentionKind => {
  const trimmed = token.trim();
  if (trimmed.startsWith('@')) return 'knowledge';
  return 'skill';
};

/**
 * 仅按正文正则定位引用串（编辑态无 selected* 时兜底高亮）
 * @param value - 正文内容
 * @returns 匹配到的引用区间列表
 */
export function findTextMentionRanges(value: string): IInlineMentionRange[] {
  if (!value) return [];
  const ranges: IInlineMentionRange[] = [];
  MENTION_TOKEN_RE.lastIndex = 0;
  let match = MENTION_TOKEN_RE.exec(value);
  let idx = 0;
  while (match) {
    const raw = match[0];
    const trigger = match[1];
    const body = match[2];
    const label = `${trigger}${body}`;
    const start = match.index + raw.length - label.length;
    const end = start + label.length;
    const kind = resolveMentionKind(label);
    ranges.push({
      key: `text:${kind}:${idx}:${start}`,
      kind,
      start,
      end,
      label,
      ids: [],
    });
    idx += 1;
    match = MENTION_TOKEN_RE.exec(value);
  }
  return ranges;
}

/**
 * 解析正文中的 mention 区间：有 selected* 时精确匹配（含历史多空格写法），否则正则兜底
 */
export function findMentionRangesInValue(
  options: ICollectOptions,
): IInlineMentionRange[] {
  const { value } = options;
  if (!value) return [];

  const hasSelected =
    (options.selectedSkills?.length ?? 0) > 0
    || (options.selectedKnowledge?.length ?? 0) > 0;

  if (hasSelected) {
    return findInlineMentionRanges(options);
  }

  return findTextMentionRanges(value);
}

/**
 * 在正文中定位已选引用串的区间（非重叠、从左扫描）
 * 仅匹配 selected* 对应 label，不用正文正则兜底——
 * 否则在句中输入 `@` / `/` 会把后续无空格正文整段误标成蓝字。
 * @param options - 收集选项（正文 + selected* 数据）
 * @returns 排序后的引用区间列表
 */
export function findInlineMentionRanges(
  options: ICollectOptions,
): IInlineMentionRange[] {
  const { value } = options;
  if (!value) return [];

  const candidates = buildCandidates(options);
  const occupied = new Array(value.length).fill(false);
  const ranges: IInlineMentionRange[] = [];

  /**
   * 检查区间是否未被占用
   * @param start - 起始位置
   * @param end - 结束位置
   */
  const isFree = (start: number, end: number): boolean => {
    for (let i = start;i < end;i += 1) {
      if (occupied[i]) return false;
    }
    return true;
  };

  /**
   * 标记区间为已占用
   * @param start - 起始位置
   * @param end - 结束位置
   */
  const mark = (start: number, end: number): void => {
    for (let i = start;i < end;i += 1) { occupied[i] = true }
  };

  candidates.forEach((candidate) => {
    let from = 0;
    while (from <= value.length - candidate.label.length) {
      const idx = value.indexOf(candidate.label, from);
      if (idx < 0) break;
      const end = idx + candidate.label.length;
      // 必须是完整 token：后接空白/结尾，避免 `/经分` 误匹配 `/经分X` 导致仍可在「技能内」插入
      const nextCh = value[end];
      const isCompleteToken =
        nextCh === undefined
        || nextCh === ' '
        || nextCh === '\n'
        || nextCh === '\r'
        || nextCh === '\t';
      if (!isCompleteToken) {
        from = idx + 1;
        continue;
      }
      if (isFree(idx, end)) {
        mark(idx, end);
        ranges.push({
          key: candidate.key,
          kind: candidate.kind,
          start: idx,
          end,
          label: candidate.label,
          ids: candidate.ids,
        });
        break;
      }
      from = idx + 1;
    }
  });

  return ranges.sort((a, b) => a.start - b.start);
}

/**
 * 判断光标/选区是否落在某个 mention 区间内
 * @param ranges - 引用区间列表
 * @param offset - 光标位置偏移量
 * @returns 命中的引用区间，或 null
 */
export function findRangeAtOffset(
  ranges: IInlineMentionRange[],
  offset: number,
): IInlineMentionRange | null {
  return (
    ranges.find((range) => offset >= range.start && offset <= range.end) || null
  );
}

/**
 * 查找完全覆盖指定选区的引用区间
 * @param ranges - 引用区间列表
 * @param start - 选区起始位置
 * @param end - 选区结束位置
 * @returns 覆盖选区的引用区间，或 null
 */
export function findRangeCoveringSelection(
  ranges: IInlineMentionRange[],
  start: number,
  end: number,
): IInlineMentionRange | null {
  if (start === end) return findRangeAtOffset(ranges, start);
  return (
    ranges.find((range) => start >= range.start && end <= range.end) || null
  );
}

/**
 * 正文已找不到对应 label 时，从 selected* 中剔除无效条目（剪枝）
 * @param options - 剪枝选项（正文 + selected* 数据）
 * @returns 剪枝后的 selected* 数据
 */
export function pruneSelectedByMissingLabels(options: {
  value: string;
  selectedSkills: ISelectedSkillTagData[];
  selectedKnowledge: ISelectedKnowledgeTagData[];
  isEN: boolean;
  kbDocUnit?: string;
}): {
    nextSkills: ISelectedSkillTagData[];
    nextKnowledge: ISelectedKnowledgeTagData[];
  } {
  const {
    value,
    selectedSkills: skillsProp,
    selectedKnowledge: knowledgeProp,
    kbDocUnit = '篇',
  } = options;
  const selectedSkills = skillsProp ?? [];
  const selectedKnowledge = knowledgeProp ?? [];

  const labelExistsInValue = (label: string): boolean => {
    if (!label) return true;
    return mentionLabelMatchVariants(label).some((variant) => value.includes(variant));
  };

  const nextSkills = selectedSkills.filter((skill) => {
    if (skill.isLoading) return true;
    // 中英文切换时正文可能仍是上一语言，需双语 label 任一命中即保留
    return [false, true].some((en) =>
      labelExistsInValue(buildSkillInlineLabel(skill, en).trim()),
    );
  });

  const byKb = new Map<string, ISelectedKnowledgeTagData[]>();
  selectedKnowledge.forEach((item) => {
    const group = byKb.get(item.kbId) || [];
    group.push(item);
    byKb.set(item.kbId, group);
  });

  const keepIds = new Set<string>();
  byKb.forEach((group) => {
    const groupLabel = buildKnowledgeInlineLabel(group, kbDocUnit).trim();
    if (groupLabel && labelExistsInValue(groupLabel)) {
      group.forEach((item) => keepIds.add(getKnowledgeTagKey(item)));
      return;
    }
    group.forEach((item) => {
      const oneLabel = buildKnowledgeInlineLabel([item], kbDocUnit).trim();
      if (oneLabel && labelExistsInValue(oneLabel)) {
        keepIds.add(getKnowledgeTagKey(item));
      }
    });
  });

  const nextKnowledge = selectedKnowledge.filter((item) =>
    keepIds.has(getKnowledgeTagKey(item)),
  );

  return { nextSkills, nextKnowledge };
}
